import type { DayView } from '../lib/day';
import { formatDate, formatLitres, formatRs } from '../lib/money';
import { DocHeader, Stamp } from '../components/PrintPreview';
import StaffStamp from '../components/StaffStamp';

/**
 * The day report, A4.
 *
 * "Day report, unapproved (dashed UNAPPROVED stamp) and approved (solid
 * APPROVED stamp with date and time): labour, diesel, misc with plant column,
 * advances, cost total and cash-out total, signature lines."
 *
 * Rendered from the day the client already has. Every figure printed here is
 * the figure stored in D1 — the Worker did the arithmetic and the rounding, so
 * a column total is exactly the sum of the rows above it.
 */

interface Plant {
  id: string;
  code: string;
  name: string;
}

interface Props {
  view: DayView;
  plants: Plant[];
  printedBy: string;
  printedAt: Date;
}

export default function DayReport({ view, plants, printedBy, printedAt }: Props) {
  const approved = view.day.closedAt !== null;
  const worked = view.entries.filter((entry) => entry.attendance === 'worked');
  const plantOf = (id: string | null) => plants.find((plant) => plant.id === id) ?? null;
  const labourSection = view.sections.find((section) => section.section === 'labour');

  const advances = view.entries.flatMap((entry) =>
    entry.advances.map((advance) => ({ entry, advance })),
  );

  return (
    <>
      <DocHeader
        doc="Quarry daily cost report"
        title={formatDate(view.day.workDate)}
        sub={`Printed ${printedAt.toLocaleString('en-GB')} by ${printedBy}`}
      />

      <Stamp approved={approved} when={view.day.closedAt} />

      {/* ------------------------------------------------------ labour */}
      <h4>Labour</h4>
      <table>
        <thead>
          <tr>
            <th>Employee</th>
            <th>Role</th>
            <th className="num">Salary</th>
            <th className="num">OT</th>
            <th className="num">Allow.</th>
            <th className="num">Sunday</th>
            <th className="num">Total earned</th>
          </tr>
        </thead>
        <tbody>
          {worked.length === 0 && (
            <tr>
              <td colSpan={7}>Nobody on site.</td>
            </tr>
          )}
          {worked.map((entry) => (
            <tr key={entry.workerId}>
              <td>{entry.workerName}</td>
              <td>
                {entry.roleName ?? '—'}
                {entry.payBasis === 'trip' && ' (allowance only)'}
              </td>
              <td className="num">{formatRs(entry.salaryRs)}</td>
              <td className="num">
                {entry.otRs ? `${entry.otTenths / 10}h ${formatRs(entry.otRs)}` : '—'}
              </td>
              <td className="num">{entry.allowanceRs ? formatRs(entry.allowanceRs) : '—'}</td>
              <td className="num">
                {entry.sundayAllowanceRs ? formatRs(entry.sundayAllowanceRs) : '—'}
              </td>
              <td className="num">{formatRs(entry.earnedRs)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={6}>Labour earned</th>
            <th className="num">{formatRs(view.totals.labourRs)}</th>
          </tr>
        </tfoot>
      </table>

      {/* ------------------------------------------------------ diesel */}
      <h4 style={{ marginTop: 14 }}>
        Diesel — rate {formatRs(view.day.dieselRateRs)}/L
      </h4>
      <table>
        <thead>
          <tr>
            <th>Plant</th>
            <th>Code</th>
            <th className="num">Litres</th>
            <th className="num">Value</th>
          </tr>
        </thead>
        <tbody>
          {view.fuel.filter((row) => row.litres_x100 > 0).length === 0 && (
            <tr>
              <td colSpan={4}>No fuel issued.</td>
            </tr>
          )}
          {view.fuel
            .filter((row) => row.litres_x100 > 0)
            .map((row) => {
              const plant = plantOf(row.plant_id);
              return (
                <tr key={row.plant_id}>
                  <td>{plant?.name ?? row.plant_name ?? '—'}{!plant && row.plant_name ? ' (removed)' : ''}</td>
                  <td>{plant?.code ?? row.plant_code ?? '—'}</td>
                  <td className="num">{formatLitres(row.litres_x100)}</td>
                  <td className="num">{formatRs(row.value_rs)}</td>
                </tr>
              );
            })}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={2}>Total</th>
            <th className="num">{formatLitres(view.totals.litresX100)} L</th>
            <th className="num">{formatRs(view.totals.dieselRs)}</th>
          </tr>
        </tfoot>
      </table>

      {/* -------------------------------------------- other expenses */}
      <h4 style={{ marginTop: 14 }}>Other expenses</h4>
      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th>Category</th>
            <th>Plant</th>
            <th>Receipt</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {view.misc.length === 0 && (
            <tr>
              <td colSpan={5}>Nothing entered.</td>
            </tr>
          )}
          {view.misc.map((expense) => (
            <tr key={expense.id}>
              <td>{expense.description}</td>
              <td>{expense.category_name}</td>
              <td>{plantOf(expense.plant_id)?.code ?? expense.plant_code ?? '—'}</td>
              <td>{expense.receipt_key ? 'Yes' : 'No'}</td>
              <td className="num">{formatRs(expense.amount_rs)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={4}>Total</th>
            <th className="num">{formatRs(view.totals.miscRs)}</th>
          </tr>
        </tfoot>
      </table>

      {/* ---------------------------------------------------- advances */}
      {advances.length > 0 && (
        <>
          <h4 style={{ marginTop: 14 }}>Advances paid in cash today</h4>
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th className="num">Advance</th>
                <th className="num">This pay week</th>
              </tr>
            </thead>
            <tbody>
              {advances.map(({ entry, advance }) => (
                <tr key={advance.id}>
                  <td>
                    {entry.workerName}
                    {advance.note && ` — ${advance.note}`}
                    {!advance.signed && ' (not signed for)'}
                  </td>
                  <td className="num">{formatRs(advance.amountRs)}</td>
                  <td className="num">{formatRs(entry.advancesThisWeekRs)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>Total</th>
                <th className="num">{formatRs(view.totals.advancesRs)}</th>
                <th />
              </tr>
            </tfoot>
          </table>
        </>
      )}

      {/* ------------------------------------------------------ totals */}
      <div className="totals">
        <div className="totline">
          <span>Labour earned today (paid weekly, not day cash)</span>
          <span>{formatRs(view.totals.labourRs)}</span>
        </div>
        <div className="totline">
          <span>Diesel + misc</span>
          <span>{formatRs(view.totals.dieselRs + view.totals.miscRs)}</span>
        </div>
        <div className="totline grand">
          <span>Total cost for the day</span>
          <span>{formatRs(view.totals.costRs)}</span>
        </div>
        {/* Boxed, because it is the number that has to match the cash box. */}
        <div className="cashbox">
          <span>Cash paid out today (diesel + misc + advances)</span>
          <span>{formatRs(view.totals.cashOutRs)}</span>
        </div>
      </div>

      {/* Signed on the screen: whoever sent labour, and whoever approved and
          closed the day, each with the signature they had saved then. */}
      <div className="siglines">
        <StaffStamp
          label="Prepared by (office)"
          userId={labourSection?.submittedBy}
          name={labourSection?.submittedByName}
          at={labourSection?.submittedAt}
        />
        <StaffStamp
          label="Approved by (owner)"
          userId={view.day.closedBy}
          name={view.day.closedByName}
          at={view.day.closedAt}
        />
      </div>
    </>
  );
}
